import React from 'react';

// Module Components
import FieldList from './FieldList';
import FieldsSection from './FieldsSection';
import PaneSearchBlock from './PaneSearchBlock';

// Hooks
import { InspectorState } from '../../../hooks';

// Types
import { FieldDefinition, FieldId, ProductCard } from '../../../types';

interface ConfigurationPaneProps {
  card: ProductCard;
  /** Field rows as the search leaves them (spec R40). */
  visibleFields: FieldDefinition[];
  searchTerm: string;
  inspector: InspectorState;
  onSearchChange: (searchTerm: string) => void;
  onToggleField: (fieldId: FieldId, enabled: boolean) => void;
}

/**
 * The configuration pane — the editor's **left** column (spec R59).
 *
 * Panel anatomy, top to bottom: the field-search block (spec R62), then the labelled
 * Fields section holding the field list (spec R62). All of it renders
 * unconditionally.
 *
 * The Card orientation section used to sit between them. It moved to the inspector
 * (`CardPropertiesPanel`) on 2026-08-15: it cost ~190px of the one column with 29
 * rows to show, and it answered a question about the *card* inside the column that
 * answers questions about *fields*. See that file for the full reasoning and the
 * spec note. Opening a field's
 * properties changes nothing here — the panel is a column of its own beside the
 * preview, mounted by `EditorBody` (spec R63, R65), so the pane is never swapped or
 * displaced by it.
 *
 * It is the first of the editor's columns in DOM order, not merely painted on the
 * left — which is what keeps tab order, reading order and an eventual RTL mirror
 * honest (ADR-2C). The pane holds no card state; it emits intents.
 *
 * Chrome: a square, flush, opaque panel closed by one hairline border on its inner
 * (right) edge, never a rounded inset card (spec R79, R80). Whether it is collapsed
 * is not its business at all — the shell owns the column's width and unmounts this
 * whole subtree while it is closed (ADR-5B, ADR-5D), so no collapse prop crosses
 * this boundary.
 *
 * It reads `inspector` for the field list only: which row opens the panel, which row
 * is the one currently open, and which row takes focus back when the panel closes.
 */
const ConfigurationPane = ({
  card,
  visibleFields,
  searchTerm,
  inspector,
  onSearchChange,
  onToggleField,
}: ConfigurationPaneProps) => (
  // From `md:` up the pane is a **bounded column**: it fills the height the editor
  // body gives it and never grows past it, so the search block and both section
  // headings stay put and the field list — the one thing inside that scrolls — takes
  // whatever height is left (spec R74). It used to be `sticky` instead, which pinned
  // it to a page scroll that R74 says must not happen at all.
  //
  // `overflow-clip`, not `overflow-hidden`: hidden IS a scroll container and
  // scroll-into-view on a focused checkbox will move it with no way back.
  // See the note on `columnWrapperClass` in EditorBody.tsx.
  // Deliberately *not* `overflow-y-auto` here as well: the field list is a drag
  // droppable, and a droppable nested inside two scrolling ancestors confuses the
  // drag library's measurements. `tw-overflow-hidden` is not a scroll container, so
  // the list stays the pane's only one.
  //
  // It states its own 320px even though the shell's wrapper states it too (spec
  // R59/Q30) — the shell owns the column, the pane owns itself, and the two agree, so
  // the pane is still the pane wherever it is mounted.
  <aside className="tw-w-full md:tw-w-80 tw-bg-surface tw-border-r tw-border-edge md:tw-h-full md:tw-flex md:tw-flex-col md:tw-overflow-clip">
    <PaneSearchBlock searchTerm={searchTerm} onSearchChange={onSearchChange} />

    <FieldsSection>
      <FieldList
        fields={visibleFields}
        enabledFieldIds={card.enabledFieldIds}
        inspectedFieldId={inspector.target}
        focusReturnFieldId={inspector.focusReturnFieldId}
        onToggleField={onToggleField}
        onOpenProperties={inspector.openFromRow}
        onFocusReturned={inspector.acknowledgeFocusReturn}
      />
    </FieldsSection>
  </aside>
);

export default ConfigurationPane;
